import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
interface ProductBackgroundAuthProps {
  mode?: 'signin' | 'signup';
  onSubmit?: (data: {
    email: string;
    password: string;
  }) => void;
}
export function ProductBackgroundAuth({
  mode = 'signin',
  onSubmit
}: ProductBackgroundAuthProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState<'sso' | 'email'>('sso');
  const productTiles = [{
    title: 'GTM Studio',
    description: 'Segment Builder',
    color: 'from-purple-500 to-purple-700',
    icon: '🎯'
  }, {
    title: 'In-Market Account Acceleration',
    description: 'Play Detail',
    color: 'from-blue-500 to-blue-700',
    icon: '⚡'
  }, {
    title: 'Copilot',
    description: 'Account Feed',
    color: 'from-green-500 to-green-700',
    icon: '🤖'
  }, {
    title: 'AI Email Composer',
    description: 'Copilot Assistant',
    color: 'from-orange-500 to-orange-700',
    icon: '✉️'
  }, {
    title: 'Plays Library',
    description: '15 plays available',
    color: 'from-cyan-500 to-cyan-700',
    icon: '📚'
  }, {
    title: 'Playbooks',
    description: '12 playbooks with signals',
    color: 'from-pink-500 to-pink-700',
    icon: '📖'
  }, {
    title: 'Forecast',
    description: 'Attainment & Pipeline',
    color: 'from-red-500 to-red-700',
    icon: '📊'
  }, {
    title: 'Integrations',
    description: 'Configuration',
    color: 'from-indigo-500 to-indigo-700',
    icon: '🧠'
  }];
  const validateEmail = (email: string) => {
    const workDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    const domain = email.split('@')[1];
    if (workDomains.includes(domain)) {
      return 'Please use your work email address';
    }
    return null;
  };
  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return null;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    if (emailError || passwordError) {
      setErrors({
        email: emailError || undefined,
        password: passwordError || undefined
      });
      return;
    }
    setErrors({});
    setIsLoading(true);
    if (onSubmit) {
      onSubmit({
        email,
        password
      });
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  };
  const handleSSOLogin = (provider: string) => {
    setIsLoading(true);
    console.log(`Logging in with ${provider}`);
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  };
  return <div className="min-h-screen w-full relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-id="element-476">
      {/* Product Background Mosaic */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-477">
        <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-8 transform rotate-3 scale-110" data-id="element-478">
          {productTiles.map((tile, index) => <div key={index} className={`bg-gradient-to-br ${tile.color} rounded-2xl p-6 shadow-2xl transform hover:scale-105 transition-all duration-300 animate-in fade-in slide-in-from-bottom`} style={{
          animationDelay: `${index * 0.1}s`,
          animationDuration: '0.6s'
        }} data-id="element-479">
              <div className="text-4xl mb-3" data-id="element-480">{tile.icon}</div>
              <h3 className="text-white font-semibold text-lg mb-1" data-id="element-481">
                {tile.title}
              </h3>
              <p className="text-white/80 text-sm" data-id="element-482">{tile.description}</p>
            </div>)}
        </div>
        {/* Dark Overlay with Blur */}
        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-xl" data-id="element-483" />
      </div>
      {/* Auth Card */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-6" data-id="element-484">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 border border-gray-200 animate-in fade-in slide-in-from-bottom duration-500" data-id="element-485">
          {/* Header */}
          <div className="text-center mb-8" data-id="element-486">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-487">
              <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg" data-id="element-488">
                Z
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-489">
              Welcome to ZoomInfo GTM
            </h1>
            <p className="text-gray-600" data-id="element-490">
              Sign in to plan and run AI-powered GTM plays
            </p>
          </div>
          {authMethod === 'sso' ? <>
              {/* SSO Options */}
              <div className="space-y-3 mb-6" data-id="element-491">
                <button onClick={() => handleSSOLogin('Google')} disabled={isLoading} className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 shadow-sm hover:shadow" data-id="element-492">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-493">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-494" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-495" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-496" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-497" />
                  </svg>
                  <span className="font-medium text-gray-700" data-id="element-498">
                    Continue with Google
                  </span>
                </button>
                <button onClick={() => handleSSOLogin('Microsoft')} disabled={isLoading} className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 shadow-sm hover:shadow" data-id="element-499">
                  <svg className="w-5 h-5" viewBox="0 0 23 23" data-id="element-500">
                    <path fill="#f3f3f3" d="M0 0h23v23H0z" data-id="element-501" />
                    <path fill="#f35325" d="M1 1h10v10H1z" data-id="element-502" />
                    <path fill="#81bc06" d="M12 1h10v10H12z" data-id="element-503" />
                    <path fill="#05a6f0" d="M1 12h10v10H1z" data-id="element-504" />
                    <path fill="#ffba08" d="M12 12h10v10H12z" data-id="element-505" />
                  </svg>
                  <span className="font-medium text-gray-700" data-id="element-506">
                    Continue with Microsoft
                  </span>
                </button>
                <button onClick={() => handleSSOLogin('Salesforce')} disabled={isLoading} className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 shadow-sm hover:shadow" data-id="element-507">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#00A1E0" data-id="element-508">
                    <path d="M10.006 5.412a4.275 4.275 0 013.656-2.115c1.194 0 2.267.5 3.028 1.303a5.946 5.946 0 013.744-1.342c3.307 0 5.988 2.681 5.988 5.988a5.97 5.97 0 01-1.105 3.456 4.986 4.986 0 011.54 3.616c0 2.763-2.24 5.003-5.003 5.003a4.98 4.98 0 01-3.616-1.54 5.97 5.97 0 01-3.456 1.105c-3.307 0-5.988-2.681-5.988-5.988 0-1.342.447-2.582 1.199-3.579a5.027 5.027 0 01-1.54-3.632c0-2.763 2.24-5.003 5.003-5.003.945 0 1.829.263 2.582.67z" data-id="element-509" />
                  </svg>
                  <span className="font-medium text-gray-700" data-id="element-510">
                    Continue with Salesforce
                  </span>
                </button>
              </div>
              {/* Divider */}
              <div className="relative mb-6" data-id="element-511">
                <div className="absolute inset-0 flex items-center" data-id="element-512">
                  <div className="w-full border-t border-gray-300" data-id="element-513" />
                </div>
                <div className="relative flex justify-center text-sm" data-id="element-514">
                  <span className="px-3 bg-white text-gray-500 font-medium" data-id="element-515">
                    Or use another method
                  </span>
                </div>
              </div>
              <button onClick={() => setAuthMethod('email')} className="w-full px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 font-medium transition-all duration-200 shadow-sm hover:shadow" data-id="element-516">
                Continue with email
              </button>
            </> : <>
              {/* Email Form */}
              <form onSubmit={handleSubmit} className="space-y-4 mb-6" data-id="element-517">
                <div data-id="element-518">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-519">
                    Email
                  </label>
                  <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@work.com" className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${errors.email ? 'border-red-500' : 'border-gray-300'}`} data-id="element-520" />
                  {errors.email && <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-id="element-521">
                      <span className="text-red-500" data-id="element-522">⚠</span>
                      {errors.email}
                    </p>}
                </div>
                <div data-id="element-523">
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-524">
                    Password
                  </label>
                  <div className="relative" data-id="element-525">
                    <input type={showPassword ? 'text' : 'password'} id="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 ${errors.password ? 'border-red-500' : 'border-gray-300'}`} data-id="element-526" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-527">
                      {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-528" /> : <EyeIcon className="w-5 h-5" data-id="element-529" />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-id="element-530">
                      <span className="text-red-500" data-id="element-531">⚠</span>
                      {errors.password}
                    </p>}
                </div>
                <button type="submit" disabled={isLoading} className="w-full px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 font-semibold transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-id="element-532">
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
              <button onClick={() => setAuthMethod('sso')} className="w-full text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors" data-id="element-533">
                ← Back to SSO options
              </button>
            </>}
          {/* Legal */}
          <p className="mt-6 text-xs text-center text-gray-600" data-id="element-534">
            By continuing, you agree to ZoomInfo's{' '}
            <Link to="/terms" className="text-red-600 hover:text-red-700 font-medium" data-id="element-535">
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-red-600 hover:text-red-700 font-medium" data-id="element-536">
              Privacy Policy
            </Link>
            .
          </p>
          {/* Footer Links */}
          <div className="mt-6 text-center" data-id="element-537">
            {mode === 'signin' ? <p className="text-sm text-gray-600" data-id="element-538">
                Don't have an account?{' '}
                <Link to="/signup" className="text-red-600 hover:text-red-700 font-semibold" data-id="element-539">
                  Sign up
                </Link>
              </p> : <p className="text-sm text-gray-600" data-id="element-540">
                Already have an account?{' '}
                <Link to="/login" className="text-red-600 hover:text-red-700 font-semibold" data-id="element-541">
                  Sign in
                </Link>
              </p>}
          </div>
        </div>
      </div>
    </div>;
}